import React from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { Grid, LineChart, XAxis, YAxis } from "react-native-svg-charts";
import moment from "moment";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { useIsMobile } from "src/hooks/ui";
import { formatMoneyShort } from "src/util/gFormatting";

const verticalContentInset = { top: 10, bottom: 10 };
const yAxisMin = 0;
const yAxisMax = 10000;
const xAxisSpacing = 30;

export const SalesLineChart: React.FC<{
  data?:
    | {
        date: string;
        value: number;
      }[]
    | undefined;
}> = ({ data }) => {
  const scrollViewRef = React.useRef<ScrollView>(null);
  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (data) {
      scrollViewRef.current?.scrollToEnd();
    }
  }, [data]);

  if (!data) {
    return (
      <View style={[styles.center, styles.flex1, styles.widthFull]}>
        <ActivityIndicator color={kColors.brandDarkGreen} size="large" />
      </View>
    );
  }

  return (
    <View style={[styles.flexRow, isMobile ? styles.heightContainerSm : styles.flex1]}>
      <YAxis
        data={data}
        style={{ marginBottom: xAxisSpacing }}
        contentInset={verticalContentInset}
        yAccessor={({ item }) => item.value}
        formatLabel={(value) => formatMoneyShort(value)}
        numberOfTicks={5}
        min={yAxisMin}
        max={yAxisMax}
        svg={{
          fill: kColors.brandGreen,
          fontSize: 14,
          fontFamily: "Poppins",
          fontWeight: "400",
        }}
      />
      <ScrollView
        ref={scrollViewRef}
        horizontal
        style={styles.ml4}
        contentContainerStyle={[styles.flexColumn, { marginLeft: xAxisSpacing, width: 100 * data.length }]}
      >
        <LineChart
          style={styles.flex1}
          data={data}
          yAccessor={({ item }) => item.value}
          yMin={yAxisMin}
          yMax={yAxisMax}
          xAccessor={({ index }) => index}
          contentInset={verticalContentInset}
          svg={{ stroke: kColors.brandGreen, strokeWidth: 3 }}
          numberOfTicks={5}
        >
          <Grid />
        </LineChart>
        <XAxis
          style={{ marginLeft: -xAxisSpacing, marginRight: -xAxisSpacing, height: xAxisSpacing }}
          data={data}
          xAccessor={({ item }) => moment(item.date, "YYYY-MM").valueOf()}
          formatLabel={(value) => moment(new Date(value)).format("MMM YY")}
          contentInset={{ left: xAxisSpacing, right: xAxisSpacing }}
          svg={{
            fill: kColors.brandGreen,
            fontSize: 14,
            fontFamily: "Poppins",
            fontWeight: "400",
          }}
        />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  center: {
    alignContent: "center",
    justifyContent: "center",
  },
  flex1: {
    flex: 1,
  },
  flexColumn: {
    flexDirection: "column",
  },
  flexRow: {
    flexDirection: "row",
  },
  heightContainerSm: {
    height: 250,
  },
  ml4: {
    marginLeft: kStyleConst.kSpacing * 4,
  },
  widthFull: {
    width: "100%",
  },
});
